import type { QueryClient, QueryKey } from '@tanstack/react-query';
import type {
  FunnelConversation,
  FunnelConversationDocument,
} from 'src/api/generated';
import { evaluateQueryMembership } from 'src/modules/shared/utils/cache/query-impact.util';
import { isInfiniteQueryData } from 'src/modules/shared/utils/cache/update.util';
import type { ConversationProjectionEntry } from '../../../types/cache/funnel-conversations/projection.types';
import {
  conversationProjection,
  getConversationImpactQuery,
  readConversationImpactField,
} from './query-impact.util';

const owners = new WeakMap<
  QueryClient,
  Map<string, ConversationProjectionEntry>
>();
const MAX_ENTRIES = 2000;
const RECHECK_MS = 30_000;
const DOCUMENT_KEY = ['crm/documents/funnel-conversations'];
const identity = (tenantId: string, id: string): string => `${tenantId}:${id}`;

/**
 * Reads accepted canonical history before an event overwrites ordinary caches.
 * @param client - Cache owner.
 * @param tenantId - Authorized tenant.
 * @param id - Conversation identity.
 * @returns Prior canonical snapshot, never an indexed timestamp comparison.
 */
export const getAcceptedConversation = (
  client: QueryClient,
  tenantId: string,
  id: number
): FunnelConversation | undefined =>
  owners.get(client)?.get(identity(tenantId, String(id)))?.conversation;

/**
 * Retains bounded authority while any board cache can receive a stale response.
 * Saturation declines new fast-path entries rather than evicting active guards.
 * Entries stay through matching responses because other queries/reads may lag.
 * @param client - Account-owned QueryClient.
 * @param id - Conversation identity from the validated envelope.
 * @param tenantId - Authorized tenant identity.
 * @param revision - Canonical occurrence time, never indexing time.
 * @param conversation - Canonical resource; absent for snapshot-free deletion.
 * @param deleted - Whether the accepted event is a tombstone.
 * @returns Whether authoritative existing-row projection is protected.
 */
export const rememberConversationEvent = (
  client: QueryClient,
  id: number,
  tenantId: string,
  revision: string,
  conversation?: FunnelConversation,
  deleted = false
): boolean => {
  if (!client.getQueryCache().findAll({ queryKey: DOCUMENT_KEY }).length) {
    return false;
  }
  let entries = owners.get(client);
  if (!entries) {
    entries = new Map();
    owners.set(client, entries);
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      if (
        event.type === 'removed' &&
        !client.getQueryCache().findAll({ queryKey: DOCUMENT_KEY }).length
      ) {
        owners.delete(client);
        unsubscribe();
      }
    });
  }
  const key = identity(tenantId, String(id));
  const previous = entries.get(key);
  const timestamp = Date.parse(revision);
  if (
    !Number.isFinite(timestamp) ||
    (previous &&
      (timestamp < previous.revision ||
        (previous.deleted && timestamp <= previous.revision)))
  ) {
    return false;
  }
  if (!previous && entries.size >= MAX_ENTRIES) return false;
  entries.set(key, {
    conversation,
    patch: conversation
      ? conversationProjection(conversation)
      : { id: String(id), tenantId },
    revision: timestamp,
    deleted,
    recheckAfter: Date.now() + RECHECK_MS,
  });
  return true;
};

/**
 * Reconciles existing search rows without changing indexed metadata or cursors.
 * Canonical rechecks resolve conflicts after the short active recovery window;
 * merely newer indexing timestamps never override an accepted resource event.
 * @param client - Owner of accepted resource snapshots.
 * @param key - Full query key used for conservative membership checks.
 * @param items - Cached or freshly fetched search rows.
 * @param onConflict - Optional bounded canonical recovery for aged HTTP conflicts.
 * @param onlyConversation - Optional event target, preserving other optimistic rows.
 * @returns Patched existing rows, excluding proven non-members and tombstones.
 */
export const reconcileConversationDocuments = (
  client: QueryClient,
  key: QueryKey,
  items: FunnelConversationDocument[],
  onConflict?: (id: number, tenantId: string, revision: string) => void,
  onlyConversation?: Pick<FunnelConversation, 'id' | 'tenantId'>
): FunnelConversationDocument[] => {
  const entries = owners.get(client);
  if (!entries) return items;
  const query = getConversationImpactQuery(key);
  return items.flatMap((item) => {
    if (
      onlyConversation &&
      (item.id !== String(onlyConversation.id) ||
        item.tenantId !== onlyConversation.tenantId)
    ) {
      return [item];
    }
    const entry = entries.get(identity(item.tenantId, item.id));
    if (!entry) return [item];
    if (entry.deleted) return [];
    const patched = { ...item, ...entry.patch };
    const changed = Object.entries(entry.patch).some(
      ([field, value]) => Reflect.get(item, field) !== value
    );
    if (changed && onConflict && Date.now() >= entry.recheckAfter) {
      entry.recheckAfter = Date.now() + RECHECK_MS;
      onConflict(
        Number(item.id),
        item.tenantId,
        new Date(entry.revision).toISOString()
      );
    }
    if (
      query &&
      evaluateQueryMembership(query, patched, readConversationImpactField) ===
        'miss'
    ) {
      return [];
    }
    return [changed ? patched : item];
  });
};

/**
 * Applies retained authority to existing rows without consuming search staleness.
 * Unchanged queries keep their state; patches never extend search freshness.
 * @param client - Owner of existing search views.
 * @param conversation - Optional accepted target; unrelated local edits stay intact.
 */
export const projectKnownConversationRows = (
  client: QueryClient,
  conversation?: Pick<FunnelConversation, 'id' | 'tenantId'>
): void => {
  client
    .getQueryCache()
    .findAll({ queryKey: DOCUMENT_KEY })
    .forEach((query) => {
      const data = client.getQueryData<unknown>(query.queryKey);
      if (!isInfiniteQueryData<FunnelConversationDocument>(data)) return;
      const pages = data.pages.map((page) => {
        const items = reconcileConversationDocuments(
          client,
          query.queryKey,
          page.items,
          undefined,
          conversation
        );
        if (
          items.length === page.items.length &&
          items.every((item, index) => item === page.items[index])
        ) {
          return page;
        }
        return { ...page, items };
      });
      if (pages.every((page, index) => page === data.pages[index])) return;
      const wasInvalidated = query.state.isInvalidated;
      client.setQueryData(
        query.queryKey,
        { ...data, pages },
        { updatedAt: query.state.dataUpdatedAt }
      );
      if (wasInvalidated) query.invalidate();
    });
};
